import { useState } from "react";
import axios from "axios";
import { assets } from "@/lib/assets";
import { Mail, Phone, MapPin, Send, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  "Consulting & Branding",
  "Professional Development",
  "Bookkeeping (BOOKKAT)",
  "Property Management (BOOKKAT)",
  "General Inquiry",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: services[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (success) setSuccess(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      toast.success("Message sent — we’ll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", service: services[0], message: "" });
      setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32">
      <div className="kat-container">
        {/* Heading row with finesse call-agent portrait */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center mb-14 md:mb-20">
          <div className="lg:col-span-8">
            <p className="overline">Start a Conversation</p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[color:var(--kat-navy)]">
              Start Your Growth Journey with Confidence.
            </h2>
            <p className="mt-6 text-base md:text-lg text-[color:var(--kat-muted)] leading-relaxed max-w-2xl">
              Complete our contact form to schedule your consultation. A team member
              will respond within one business day.
            </p>
          </div>

          <div className="lg:col-span-4">
            <div className="relative w-52 h-52 md:w-64 md:h-64 mx-auto lg:ml-auto">
              <div
                className="absolute -inset-3 rounded-full"
                style={{
                  background:
                    "conic-gradient(from 210deg, #0A192F, #38BDF8, #0A192F)",
                }}
                aria-hidden
              />
              <img
                src={assets.callAgent}
                alt="A KAT client-success agent"
                data-testid="contact-agent-image"
                className="relative w-full h-full object-cover object-top rounded-full shadow-2xl border-4 border-white"
              />
              <div className="absolute -bottom-3 -left-3 md:-left-6 bg-white border border-slate-200 rounded-full px-4 py-1.5 shadow-md">
                <span className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[color:var(--kat-navy)]">
                  We’re listening
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick facts */}
        <ul className="grid sm:grid-cols-3 gap-5 mb-12 max-w-4xl">
          <li className="flex items-start gap-3">
            <span className="w-10 h-10 rounded-sm border border-slate-300 flex items-center justify-center text-[color:var(--kat-navy)] shrink-0">
              <Mail size={16} strokeWidth={1.6} />
            </span>
            <div>
              <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[color:var(--kat-muted)]">
                Email
              </p>
              <a
                href="mailto:karlene@tovocomm.com"
                data-testid="contact-email-link"
                className="text-[color:var(--kat-navy)] font-medium hover:text-[color:var(--kat-sky-deep)] break-all"
              >
                karlene@tovocomm.com
              </a>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-10 h-10 rounded-sm border border-slate-300 flex items-center justify-center text-[color:var(--kat-navy)] shrink-0">
              <Phone size={16} strokeWidth={1.6} />
            </span>
            <div>
              <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[color:var(--kat-muted)]">
                Hours
              </p>
              <p className="text-[color:var(--kat-navy)] font-medium">
                Mon – Fri · 9:00 AM – 6:00 PM
              </p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-10 h-10 rounded-sm border border-slate-300 flex items-center justify-center text-[color:var(--kat-navy)] shrink-0">
              <MapPin size={16} strokeWidth={1.6} />
            </span>
            <div>
              <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[color:var(--kat-muted)]">
                Serving
              </p>
              <p className="text-[color:var(--kat-navy)] font-medium">
                Nationwide.
              </p>
            </div>
          </li>
        </ul>

        {/* Form (full width, single column) */}
        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="grid gap-5 border border-slate-200 bg-white p-6 md:p-10 rounded-sm max-w-4xl"
        >
          <div className="grid md:grid-cols-2 gap-5">
            <div className="flex flex-col gap-2">
              <label htmlFor="c-name" className="text-xs tracking-[0.22em] uppercase font-semibold text-[color:var(--kat-muted)]">
                Full Name
              </label>
              <input
                id="c-name"
                name="name"
                type="text"
                required
                value={form.name}
                onChange={onChange}
                data-testid="contact-input-name"
                placeholder="e.g. Jordan Rivera"
                className="h-12 px-4 border border-slate-300 rounded-sm bg-white text-[color:var(--kat-navy)] placeholder:text-slate-400 focus:outline-none focus:border-[color:var(--kat-navy)] transition-colors"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="c-email" className="text-xs tracking-[0.22em] uppercase font-semibold text-[color:var(--kat-muted)]">
                Email
              </label>
              <input
                id="c-email"
                name="email"
                type="email"
                required
                value={form.email}
                onChange={onChange}
                data-testid="contact-input-email"
                placeholder="you@company.com"
                className="h-12 px-4 border border-slate-300 rounded-sm bg-white text-[color:var(--kat-navy)] placeholder:text-slate-400 focus:outline-none focus:border-[color:var(--kat-navy)] transition-colors"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div className="flex flex-col gap-2">
              <label htmlFor="c-phone" className="text-xs tracking-[0.22em] uppercase font-semibold text-[color:var(--kat-muted)]">
                Phone (optional)
              </label>
              <input
                id="c-phone"
                name="phone"
                type="tel"
                value={form.phone}
                onChange={onChange}
                data-testid="contact-input-phone"
                placeholder="+1 (555) 123-4567"
                className="h-12 px-4 border border-slate-300 rounded-sm bg-white text-[color:var(--kat-navy)] placeholder:text-slate-400 focus:outline-none focus:border-[color:var(--kat-navy)] transition-colors"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="c-service" className="text-xs tracking-[0.22em] uppercase font-semibold text-[color:var(--kat-muted)]">
                Interested In
              </label>
              <select
                id="c-service"
                name="service"
                value={form.service}
                onChange={onChange}
                data-testid="contact-input-service"
                className="h-12 px-4 border border-slate-300 rounded-sm bg-white text-[color:var(--kat-navy)] focus:outline-none focus:border-[color:var(--kat-navy)] transition-colors"
              >
                {services.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="c-message" className="text-xs tracking-[0.22em] uppercase font-semibold text-[color:var(--kat-muted)]">
              Message
            </label>
            <textarea
              id="c-message"
              name="message"
              required
              rows={5}
              value={form.message}
              onChange={onChange}
              data-testid="contact-input-message"
              placeholder="Tell us a bit about your goals, your team, or the project you have in mind…"
              className="px-4 py-3 border border-slate-300 rounded-sm bg-white text-[color:var(--kat-navy)] placeholder:text-slate-400 focus:outline-none focus:border-[color:var(--kat-navy)] transition-colors resize-y"
            />
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
            <p className="text-xs text-[color:var(--kat-muted)]">
              By submitting, you agree to be contacted regarding your inquiry.
            </p>
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-btn"
              className="inline-flex items-center gap-2 bg-[color:var(--kat-navy)] hover:bg-[color:var(--kat-navy-2)] disabled:opacity-70 disabled:cursor-not-allowed text-white text-sm font-medium px-8 py-3.5 rounded-sm transition-colors kat-focus"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Sending…
                </>
              ) : success ? (
                <>
                  <CheckCircle2 size={16} /> Sent — Send Another
                </>
              ) : (
                <>
                  Send Message <Send size={14} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default Contact;
